import { ref, onValue, onDisconnect, runTransaction, set, remove } from 'firebase/database';
import { realtimeDatabase } from '../database/firebase';

/** Firebase RTDB keys cannot contain . # $ [ ] / */
export function sanitizeFirebaseKey(value) {
  return String(value ?? 'unknown').replace(/[.#$/[\]]/g, '_');
}

/** Same org + session → same live board for all players. */
export function buildFirebaseSessionId(organizationId, sessionId) {
  const org = sanitizeFirebaseKey(organizationId || 'default');
  const sess = sanitizeFirebaseKey(sessionId);
  return `${org}__${sess}`;
}

export function sessionUsersRef(firebaseSessionId) {
  return ref(realtimeDatabase, `session/${firebaseSessionId}/users`);
}

export function userCanvasRef(firebaseSessionId, firebaseUserId) {
  return ref(
    realtimeDatabase,
    `session/${firebaseSessionId}/users/${sanitizeFirebaseKey(firebaseUserId)}`
  );
}

export function subscribeSessionUsers(firebaseSessionId, onData) {
  if (!firebaseSessionId) return () => {};
  return onValue(sessionUsersRef(firebaseSessionId), (snapshot) => {
    onData(snapshot.val() || {});
  });
}

function statusRank(status) {
  if (status === 'submitted' || status === 'auto_filled') return 2;
  if (status === 'assigned') return 1;
  return 0;
}

async function mergeUserCanvasState(firebaseSessionId, firebaseUserId, payload) {
  if (!firebaseSessionId || !firebaseUserId) return;
  const userRef = userCanvasRef(firebaseSessionId, firebaseUserId);
  // runTransaction, not get()+set() — the previous read-then-write was two
  // separate round trips, so an older in-flight patch (e.g. a live drawing
  // preview, pushed every few strokes) could read the node BEFORE a newer
  // patch (e.g. the final submission) committed, then write its own merged
  // object AFTER — silently reverting status/tileImageKey back to the
  // pre-submission value since its merge base was already stale. A
  // transaction always merges onto whatever the current committed value
  // actually is, retrying automatically if another write lands first.
  await runTransaction(userRef, (current) => {
    const next = {
      ...(current || {}),
      ...payload,
      updatedAt: Date.now(),
    };
    // A finished tile never goes back to an earlier state. A reload that re-registers
    // this player as 'assigned' (or a late preview patch) must not wipe a committed
    // 'submitted' / 'auto_filled' status and its image off the board.
    if (current && payload.status != null && statusRank(current.status) > statusRank(payload.status)) {
      next.status = current.status;
      if (current.tileImageKey) next.tileImageKey = current.tileImageKey;
    }
    return next;
  });
}

/** Seed (or refresh) this player's presence node. status: 'waiting'|'assigned'|'submitted'|'auto_filled' */
export async function registerUserCanvasSession(firebaseSessionId, firebaseUserId, payload) {
  await mergeUserCanvasState(firebaseSessionId, firebaseUserId, {
    status: 'waiting',
    ...payload,
  });
}

/** Merge-write a status update, e.g. {status: 'submitted', tileImageKey}. */
export async function patchUserCanvasState(firebaseSessionId, firebaseUserId, payload) {
  await mergeUserCanvasState(firebaseSessionId, firebaseUserId, payload);
}

/**
 * Arms Firebase's own onDisconnect so a player's presence node is removed the
 * moment their connection actually drops (closed tab, lost network) — not
 * just when this component happens to unmount. Without this, a closed
 * waiting-room tab left a permanent node behind: "Players Joined" kept
 * counting them, and endCanvasActivity's presentUserIds (built from every
 * node) treated them as present, so the lobby-timeout auto-fill skipped their
 * tile entirely, leaving it stuck unfinished with no player and no fallback.
 *
 * Scoped to the WAITING ROOM phase only — the returned canceller must be
 * called once the player moves on into actual gameplay (normal unmount while
 * the tab stays open), so a later transient network blip during drawing
 * doesn't wipe their in-progress tile state from the live board.
 */
export function armWaitingRoomDisconnectCleanup(firebaseSessionId, firebaseUserId) {
  if (!firebaseSessionId || !firebaseUserId) return () => {};
  const userRef = userCanvasRef(firebaseSessionId, firebaseUserId);
  const disconnectHandle = onDisconnect(userRef);
  disconnectHandle.remove().catch((err) =>
    console.warn('Failed to arm waiting-room disconnect cleanup:', err)
  );
  return () => {
    disconnectHandle.cancel().catch(() => {
      /* ignore — connection may already be gone */
    });
  };
}

function canvasUserScore(data) {
  if (!data || typeof data !== 'object') return -1;
  const status = data.status;
  const updatedAt = Number(data.updatedAt) || 0;
  const statusRank = status === 'submitted' || status === 'auto_filled' ? 2 : status === 'assigned' ? 1 : 0;
  return statusRank * 1e12 + updatedAt;
}

/** One representative user per tile (the most-progressed one, in case of duplicate assignment). */
export function usersByTileId(usersMap) {
  const byTile = {};
  if (!usersMap || typeof usersMap !== 'object') return byTile;
  Object.entries(usersMap).forEach(([, data]) => {
    const tileId = Math.floor(Number(data?.puzzle_id));
    if (!Number.isFinite(tileId) || tileId < 1) return;
    const existing = byTile[tileId];
    if (!existing || canvasUserScore(data) > canvasUserScore(existing)) {
      byTile[tileId] = data;
    }
  });
  return byTile;
}

// ---- Waiting room (Live mode lobby) ----
// session/{firebaseSessionId}/waitingRoom = { startedAt, durationSeconds, resetNonce, skipped }
// startedAt is only ever set by an explicit admin action (Start/Restart/Skip below) —
// players joining the lobby never seed it themselves, so the countdown stays paused
// (showing "waiting for host to start") until the admin starts it for everyone.

export function waitingRoomRef(firebaseSessionId) {
  return ref(realtimeDatabase, `session/${firebaseSessionId}/waitingRoom`);
}

/** Admin action: start (or restart) the countdown from the full configured duration for everyone waiting. */
export async function resetWaitingRoom(firebaseSessionId, durationSeconds) {
  if (!firebaseSessionId) return;
  await runTransaction(waitingRoomRef(firebaseSessionId), (current) => ({
    startedAt: Date.now(),
    durationSeconds,
    resetNonce: (current?.resetNonce || 0) + 1,
  }));
}

/** Admin action: skip the lobby countdown immediately, sending everyone to the canvas now. */
export async function skipWaitingRoom(firebaseSessionId) {
  if (!firebaseSessionId) return;
  await runTransaction(waitingRoomRef(firebaseSessionId), (current) => ({
    startedAt: Date.now(),
    durationSeconds: 0,
    resetNonce: (current?.resetNonce || 0) + 1,
    skipped: true,
  }));
}

export function subscribeWaitingRoom(firebaseSessionId, onData) {
  if (!firebaseSessionId) return () => {};
  return onValue(waitingRoomRef(firebaseSessionId), (snapshot) => {
    onData(snapshot.val() || null);
  });
}

// ---- Canvas drawing phase (Live mode) ----
// session/{firebaseSessionId}/canvasActivity = { startedAt, durationSeconds }
// A single shared clock for the whole session's drawing phase, seeded once by
// whichever player reaches the canvas first (race-safe transaction) — every
// other player, including one who arrives well after the others, reads that
// same startedAt instead of getting their own fresh personal timer. That's
// what lets a late joiner be told "this session's drawing time is already up"
// instead of silently being handed a brand new 5 minutes to draw alone.

export function canvasActivityRef(firebaseSessionId) {
  return ref(realtimeDatabase, `session/${firebaseSessionId}/canvasActivity`);
}

// ---- Live previews ----
// session/{firebaseSessionId}/previews/{userKey} = { dataUrl, updatedAt }
// Kept OUT of session/{id}/users on purpose: that node is small (status, name,
// piece, sub-slot) and every board listens to it, whereas a preview is a JPEG
// data URL. A viewer subscribes to only a handful of previews (see
// pickLiveSample in utils/subSlots.js), so one player's stroke is delivered to
// the few people watching them, not broadcast to the whole session.

export function previewRef(firebaseSessionId, firebaseUserId) {
  return ref(
    realtimeDatabase,
    `session/${firebaseSessionId}/previews/${sanitizeFirebaseKey(firebaseUserId)}`
  );
}

/** Plain overwrite (no transaction): a newer frame simply replaces an older one. */
export async function writeLivePreview(firebaseSessionId, firebaseUserId, dataUrl) {
  if (!firebaseSessionId || !firebaseUserId || !dataUrl) return;
  const node = previewRef(firebaseSessionId, firebaseUserId);
  armPreviewCleanupOnce(firebaseSessionId, firebaseUserId, node);
  await set(node, { dataUrl, updatedAt: Date.now() });
}

// A preview is a ~10-50 KB blob. If the player's tab dies mid-drawing nothing else
// ever deletes it, so it would sit in the database (and be billed) forever and
// show a stale "live" drawing. Ask Firebase to remove it when the connection drops.
// Armed once per node per page load (re-arming on every write is wasted traffic).
const armedPreviewCleanups = new Set();
function armPreviewCleanupOnce(firebaseSessionId, firebaseUserId, node) {
  const key = `${firebaseSessionId}/${firebaseUserId}`;
  if (armedPreviewCleanups.has(key)) return;
  armedPreviewCleanups.add(key);
  onDisconnect(node)
    .remove()
    .catch(() => armedPreviewCleanups.delete(key));
}

/** Drops the preview once the tile is final (the finished image replaces it). */
export async function clearLivePreview(firebaseSessionId, firebaseUserId) {
  if (!firebaseSessionId || !firebaseUserId) return;
  await remove(previewRef(firebaseSessionId, firebaseUserId));
}

/** Subscribes to ONE player's preview; cb receives the data URL or null. */
export function subscribeLivePreview(firebaseSessionId, firebaseUserId, cb) {
  if (!firebaseSessionId || !firebaseUserId) return () => {};
  return onValue(previewRef(firebaseSessionId, firebaseUserId), (snapshot) => {
    cb(snapshot.val()?.dataUrl || null);
  });
}

/** Seed the shared drawing countdown exactly once (race-safe for simultaneous first arrivals). */
export async function ensureCanvasActivityStarted(firebaseSessionId, durationSeconds, startedAt = null) {
  if (!firebaseSessionId) return null;
  // `startedAt` (client-clock ms) lets the caller seed the board with the
  // server-authoritative start instead of this browser's own Date.now().
  const seededStart = Number.isFinite(Number(startedAt)) && Number(startedAt) > 0 ? Number(startedAt) : null;
  const result = await runTransaction(canvasActivityRef(firebaseSessionId), (current) => {
    if (current && current.startedAt) return current;
    return { startedAt: seededStart ?? Date.now(), durationSeconds };
  });
  return result.snapshot.val();
}
